import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { RotateCcw, Trophy } from 'lucide-react';

interface Card {
  id: string;
  suit: 'hearts' | 'diamonds' | 'clubs' | 'spades';
  value: number;
  isRed: boolean;
  faceUp: boolean;
}

interface SolitaireProps {
  onClose?: () => void;
}

export const Solitaire: React.FC<SolitaireProps> = ({ onClose }) => {
  const [deck, setDeck] = useState<Card[]>([]);
  const [tableau, setTableau] = useState<Card[][]>([[], [], [], [], [], [], []]);
  const [foundation, setFoundation] = useState<Card[][]>([[], [], [], []]);
  const [stock, setStock] = useState<Card[]>([]);
  const [waste, setWaste] = useState<Card[]>([]);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [selectedPile, setSelectedPile] = useState<string | null>(null);
  const [moves, setMoves] = useState(0);
  const [gameWon, setGameWon] = useState(false);

  const suits = ['hearts', 'diamonds', 'clubs', 'spades'] as const;
  const suitSymbols = { hearts: '♥', diamonds: '♦', clubs: '♣', spades: '♠' };
  const suitColors = { hearts: true, diamonds: true, clubs: false, spades: false };

  useEffect(() => {
    initializeGame();
  }, []);

  useEffect(() => {
    // Check for win condition
    const allFoundationComplete = foundation.every(pile => pile.length === 13);
    if (allFoundationComplete && !gameWon) {
      setGameWon(true);
    }
  }, [foundation, gameWon]);

  const createDeck = (): Card[] => {
    const newDeck: Card[] = [];
    suits.forEach(suit => {
      for (let value = 1; value <= 13; value++) {
        newDeck.push({
          id: `${suit}-${value}`,
          suit,
          value,
          isRed: suitColors[suit],
          faceUp: false
        });
      }
    });
    return shuffleArray(newDeck);
  };

  const shuffleArray = <T,>(array: T[]): T[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const initializeGame = () => {
    const newDeck = createDeck();
    const newTableau: Card[][] = [[], [], [], [], [], [], []];
    let deckIndex = 0;

    // Deal cards to tableau
    for (let pile = 0; pile < 7; pile++) {
      for (let card = 0; card <= pile; card++) {
        const currentCard = { ...newDeck[deckIndex] };
        if (card === pile) {
          currentCard.faceUp = true;
        }
        newTableau[pile].push(currentCard);
        deckIndex++;
      }
    }

    const remainingCards = newDeck.slice(deckIndex);
    
    setTableau(newTableau);
    setFoundation([[], [], [], []]);
    setStock(remainingCards);
    setWaste([]);
    setSelectedCard(null);
    setSelectedPile(null);
    setMoves(0);
    setGameWon(false);
  };

  const drawCard = () => {
    if (stock.length > 0) {
      const drawnCard = { ...stock[0], faceUp: true };
      setWaste([drawnCard, ...waste]);
      setStock(stock.slice(1));
    } else if (waste.length > 0) {
      // Reset stock from waste
      const newStock = waste.map(card => ({ ...card, faceUp: false })).reverse();
      setStock(newStock);
      setWaste([]);
    }
    setMoves(moves + 1);
  };

  const canPlaceOnTableau = (card: Card, targetCard: Card): boolean => {
    return card.value === targetCard.value - 1 && card.isRed !== targetCard.isRed;
  };

  const canPlaceOnFoundation = (card: Card, foundationPile: Card[]): boolean => {
    if (foundationPile.length === 0) {
      return card.value === 1; // Ace
    }
    const topCard = foundationPile[foundationPile.length - 1];
    return card.suit === topCard.suit && card.value === topCard.value + 1;
  };

  const handleCardClick = (card: Card, pileType: string, pileIndex?: number) => {
    if (!card.faceUp) return;

    if (selectedCard && selectedPile) {
      // Try to place selected card
      if (pileType === 'tableau' && pileIndex !== undefined) {
        const targetPile = tableau[pileIndex];
        if (targetPile.length === 0 && selectedCard.value === 13) {
          // King can go on empty tableau pile
          moveCard();
        } else if (targetPile.length > 0 && canPlaceOnTableau(selectedCard, targetPile[targetPile.length - 1])) {
          moveCard();
        }
      } else if (pileType === 'foundation' && pileIndex !== undefined) {
        if (canPlaceOnFoundation(selectedCard, foundation[pileIndex])) {
          moveCard();
        }
      }
      setSelectedCard(null);
      setSelectedPile(null);
    } else {
      // Select card
      setSelectedCard(card);
      setSelectedPile(`${pileType}-${pileIndex}`);
    }
  };

  const moveCard = () => {
    setMoves(moves + 1);
    // Implementation would move the card between piles
    // Simplified for this demo
  };

  const getCardDisplay = (card: Card) => {
    if (!card.faceUp) {
      return (
        <div className="w-16 h-22 bg-blue-600 border-2 border-gray-300 rounded-lg flex items-center justify-center">
          <div className="w-12 h-16 bg-blue-800 rounded border border-blue-400"></div>
        </div>
      );
    }

    const valueDisplay = card.value === 1 ? 'A' : 
                        card.value === 11 ? 'J' :
                        card.value === 12 ? 'Q' :
                        card.value === 13 ? 'K' : 
                        card.value.toString();

    return (
      <div 
        className={`w-16 h-22 bg-white border-2 rounded-lg flex flex-col items-center justify-between p-1 cursor-pointer hover:bg-gray-50 ${
          selectedCard?.id === card.id ? 'ring-2 ring-blue-500' : 'border-gray-300'
        }`}
        onClick={() => handleCardClick(card, 'tableau', 0)}
      >
        <div className={`text-sm font-bold ${card.isRed ? 'text-red-500' : 'text-black'}`}>
          {valueDisplay}
        </div>
        <div className={`text-lg ${card.isRed ? 'text-red-500' : 'text-black'}`}>
          {suitSymbols[card.suit]}
        </div>
        <div className={`text-sm font-bold ${card.isRed ? 'text-red-500' : 'text-black'} rotate-180`}>
          {valueDisplay}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full h-full bg-gradient-to-br from-green-600 to-green-800 p-4 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 bg-black/20 rounded-lg p-3">
        <div className="flex items-center gap-4">
          <h2 className="text-white font-bold text-lg">Solitaire</h2>
          <div className="text-white text-sm">Moves: {moves}</div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={initializeGame}
            className="bg-white/10 text-white border-white/20 hover:bg-white/20"
          >
            <RotateCcw size={16} className="mr-1" />
            New Game
          </Button>
          {onClose && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            >
              Close
            </Button>
          )}
        </div>
      </div>

      {/* Win Message */}
      {gameWon && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 text-center">
            <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">Congratulations!</h3>
            <p className="text-gray-600 mb-4">You won in {moves} moves!</p>
            <Button onClick={initializeGame}>Play Again</Button>
          </div>
        </div>
      )}

      {/* Game Area */}
      <div className="space-y-6">
        {/* Top Row: Stock, Waste, and Foundation */}
        <div className="flex justify-between">
          <div className="flex gap-2">
            {/* Stock */}
            <div 
              className="w-16 h-22 border-2 border-dashed border-white/30 rounded-lg flex items-center justify-center cursor-pointer hover:bg-white/10"
              onClick={drawCard}
            >
              {stock.length > 0 ? (
                <div className="w-12 h-16 bg-blue-600 rounded border border-blue-400"></div>
              ) : (
                <RotateCcw className="text-white/50" size={20} />
              )}
            </div>
            
            {/* Waste */}
            <div className="w-16 h-22 border-2 border-dashed border-white/30 rounded-lg flex items-center justify-center">
              {waste.length > 0 && getCardDisplay(waste[0])}
            </div>
          </div>

          {/* Foundation */}
          <div className="flex gap-2">
            {[0, 1, 2, 3].map(i => (
              <div 
                key={i}
                className="w-16 h-22 border-2 border-dashed border-white/30 rounded-lg flex items-center justify-center"
              >
                {foundation[i].length > 0 && getCardDisplay(foundation[i][foundation[i].length - 1])}
              </div>
            ))}
          </div>
        </div>

        {/* Tableau */}
        <div className="flex gap-2 justify-center">
          {tableau.map((pile, pileIndex) => (
            <div key={pileIndex} className="flex flex-col">
              <div className="w-16 h-22 border-2 border-dashed border-white/30 rounded-lg mb-2"></div>
              <div className="relative">
                {pile.map((card, cardIndex) => (
                  <div 
                    key={card.id}
                    className="absolute"
                    style={{ top: `${cardIndex * 20}px` }}
                  >
                    {getCardDisplay(card)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-8 bg-black/20 rounded-lg p-4">
        <h3 className="text-white font-semibold mb-2">How to Play:</h3>
        <ul className="text-white/80 text-sm space-y-1">
          <li>• Click the deck to draw cards</li>
          <li>• Build foundation piles from Ace to King by suit</li>
          <li>• Build tableau columns in descending order, alternating colors</li>
          <li>• Only Kings can go on empty tableau columns</li>
          <li>• Win by moving all cards to the foundation piles</li>
        </ul>
      </div>
    </div>
  );
};